import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { API_ENDPOINTS } from "../../../constants/config"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { ChevronDown, ChevronUp } from "lucide-react"

function FeeStructureTable() {
  const [fees, setFees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [newFeeType, setNewFeeType] = useState(false)
  const [newFeeName, setNewFeeName] = useState("")
  const [newFeeAmount, setNewFeeAmount] = useState("")
  const [newFeeTypeSelect, setNewFeeTypeSelect] = useState("Monthly")
  const [quarterValues, setQuarterValues] = useState([3, 3, 3, 3]) // Default to 3 months per quarter
  const [quarterError, setQuarterError] = useState("")
  const [selectedClass, setSelectedClass] = useState("")
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("")
  const [classes, setClasses] = useState([])
  const [academicYears, setAcademicYears] = useState([])
  const [expandedFeeId, setExpandedFeeId] = useState(null)
  const [feeQuarterValues, setFeeQuarterValues] = useState({})

  useEffect(() => {
    const fetchClassesAndYears = async () => {
      try {
        const classesResponse = await fetch(API_ENDPOINTS.classes, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
        if (!classesResponse.ok) throw new Error("Failed to fetch classes")
        const classesData = await classesResponse.json()
        setClasses(classesData.data.classes)

        const yearsResponse = await fetch(API_ENDPOINTS.academicYears, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
        if (!yearsResponse.ok) throw new Error("Failed to fetch academic years")
        const yearsData = await yearsResponse.json()

        const currentYear = yearsData.data.academicYears.find(
          (year) => year.isCurrent
        )
        if (!currentYear) throw new Error("No current academic year found")

        setAcademicYears([currentYear])
        setSelectedAcademicYear(currentYear._id)
        if (classesData.data.classes.length > 0) {
          setSelectedClass(classesData.data.classes[0]._id)
        }
      } catch (err) {
        setError("Failed to load classes and academic years")
      }
    }

    fetchClassesAndYears()
  }, [])

  useEffect(() => {
    const fetchFees = async () => {
      if (!selectedClass || !selectedAcademicYear) return
      setLoading(true)
      try {
        const response = await fetch(
          API_ENDPOINTS.feesByClass(selectedClass, selectedAcademicYear),
          {
            credentials: "include",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || "Failed to fetch fees")
        }

        const data = await response.json()

        console.log("Fetched fees data:", data.data.fees);
        const transformedFees = data.data.fees.map((fee) => ({
          id: fee._id,
          name: fee.fee_name,
          amount: fee.fee_amount.toString(),
          type: fee.fee_type,
          quarter_details: fee.quarter_details || (
            fee.fee_type === "Annually" 
              ? [fee.fee_amount / 4, fee.fee_amount / 4, fee.fee_amount / 4, fee.fee_amount / 4]
              : [3, 3, 3, 3] // Default to 3 months per quarter for monthly fees
          ),
          changed: false,
        }))

        // Initialize quarter values for each fee
        const quarterValuesObj = {}
        transformedFees.forEach(fee => {
          quarterValuesObj[fee.id] = [...fee.quarter_details]
        })
        
        setFeeQuarterValues(quarterValuesObj)
        setFees(transformedFees)
        setError("")
      } catch (err) {
        setError("Failed to load fees")
      } finally {
        setLoading(false)
      }
    }

    fetchFees()
  }, [selectedClass, selectedAcademicYear])

  // Validate quarterly amounts when they change
  useEffect(() => {
    if (newFeeTypeSelect === "Annually" && newFeeAmount) {
      const totalAmount = Number(newFeeAmount)
      const quarterSum = quarterValues.reduce((sum, val) => sum + (val || 0), 0)
      
      if (quarterSum !== 0 && Math.abs(quarterSum - totalAmount) > 0.01) {
        setQuarterError(`The sum of quarterly amounts (${quarterSum}) does not match the total fee amount (${totalAmount})`)
      } else {
        setQuarterError("")
      }
    } else {
      setQuarterError("")
    }
  }, [quarterValues, newFeeAmount, newFeeTypeSelect])

  // Validate fee quarter values when they change
  const validateFeeQuarterValues = (feeId, feeAmount, feeType) => {
    if (!feeQuarterValues[feeId]) return ""
    
    if (feeType === "Annually") {
      const totalAmount = Number(feeAmount)
      const quarterSum = feeQuarterValues[feeId].reduce((sum, val) => sum + (val || 0), 0)
      
      if (quarterSum !== 0 && Math.abs(quarterSum - totalAmount) > 0.01) {
        return `The sum of quarterly amounts (${quarterSum}) does not match the total fee amount (${totalAmount})`
      }
    }
    
    return ""
  }

  const handleInputChange = (id, field, value) => {
    setFees((prevFees) =>
      prevFees.map((fee) =>
        fee.id === id ? { ...fee, [field]: value, changed: true } : fee
      )
    )
  }

  const handleQuarterValueChange = (feeId, index, value) => {
    const numValue = parseFloat(value || 0)
    
    setFeeQuarterValues(prev => {
      const updated = { ...prev }
      if (!updated[feeId]) {
        updated[feeId] = [3, 3, 3, 3] // Default for monthly
      }
      updated[feeId] = [...updated[feeId]]
      updated[feeId][index] = numValue
      return updated
    })
    
    // Mark fee as changed
    setFees(prevFees => 
      prevFees.map(fee => 
        fee.id === feeId ? { ...fee, changed: true } : fee
      )
    )
  }

  const distributeEvenlyForFee = (feeId) => {
    const fee = fees.find(f => f.id === feeId)
    if (!fee) return
    
    if (fee.type === "Annually") {
      const amount = Number(fee.amount)
      const evenAmount = amount / 4
      
      setFeeQuarterValues(prev => ({
        ...prev,
        [feeId]: [evenAmount, evenAmount, evenAmount, evenAmount]
      }))
    } else if (fee.type === "Monthly") {
      // Distribute 12 months evenly across 4 quarters
      setFeeQuarterValues(prev => ({
        ...prev,
        [feeId]: [3, 3, 3, 3]
      }))
    }
    
    // Mark fee as changed
    setFees(prevFees => 
      prevFees.map(fee => 
        fee.id === feeId ? { ...fee, changed: true } : fee
      )
    )
  }

  const handleAddNewType = () => {
    setNewFeeType(true)
    setQuarterValues(newFeeTypeSelect === "Monthly" ? [3, 3, 3, 3] : [0, 0, 0, 0])
    setQuarterError("")
  }

  const handleCancelNewType = () => {
    setNewFeeType(false)
    setNewFeeName("")
    setNewFeeAmount("")
    setNewFeeTypeSelect("Monthly")
    setQuarterValues([3, 3, 3, 3])
    setQuarterError("")
  }

  const handleSaveNewType = async () => {
    if (!newFeeName || !newFeeAmount || !selectedClass || !selectedAcademicYear) {
      toast.error("Please fill all required fields")
      return
    }

    // Validate quarterly amounts for annual fees
    if (newFeeTypeSelect === "Annually") {
      const totalAmount = Number(newFeeAmount)
      const quarterSum = quarterValues.reduce((sum, val) => sum + (val || 0), 0)
      
      if (quarterSum !== 0 && Math.abs(quarterSum - totalAmount) > 0.01) {
        toast.error("The sum of quarterly amounts must match the total fee amount")
        return
      }
    }

    // Prepare quarterly data
    let quarterlyBreakdown = null
    if (newFeeTypeSelect === "Annually") {
      // If quarters are not specified, distribute evenly
      if (quarterValues.every(val => val === 0)) {
        const defaultQuarter = Number(newFeeAmount) / 4
        quarterlyBreakdown = [defaultQuarter, defaultQuarter, defaultQuarter, defaultQuarter]
      } else {
        quarterlyBreakdown = quarterValues
      }
    } else if (newFeeTypeSelect === "Monthly") {
      quarterlyBreakdown = quarterValues // use as multipliers
    }

    try {
      const response = await fetch(API_ENDPOINTS.fees, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({
          class_id: selectedClass,
          academic_year: selectedAcademicYear,
          fee_name: newFeeName,
          fee_amount: Number(newFeeAmount),
          fee_type: newFeeTypeSelect,
          quarter_details: quarterlyBreakdown,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to create fee")
      }

      const data = await response.json()
      const newFee = {
        id: data.data.fee._id,
        name: data.data.fee.fee_name,
        amount: data.data.fee.fee_amount.toString(),
        type: data.data.fee.fee_type,
        quarter_details: data.data.fee.quarter_details || quarterlyBreakdown,
        changed: false,
      }

      setFees((prevFees) => [...prevFees, newFee])
      
      // Initialize quarter values for the new fee
      setFeeQuarterValues(prev => ({
        ...prev,
        [newFee.id]: [...(newFee.quarter_details || quarterlyBreakdown)]
      }))

      handleCancelNewType()
      toast.success("Fee added successfully")
    } catch (err) {
      toast.error(err.message || "Failed to create fee")
    }
  }

  const handleUpdateFee = async (id) => {
    const feeToUpdate = fees.find((fee) => fee.id === id)
    if (!feeToUpdate) return

    // Validate quarterly amounts
    if (feeToUpdate.type === "Annually") {
      const errorMsg = validateFeeQuarterValues(id, feeToUpdate.amount, feeToUpdate.type)
      if (errorMsg) {
        toast.error(errorMsg)
        return
      }
    }

    try {
      const updateData = {
        fee_name: feeToUpdate.name,
        fee_amount: Number(feeToUpdate.amount),
        fee_type: feeToUpdate.type,
      }

      // Add quarter_details for both annual and monthly fees
      console.log("Quarter values for update:", feeQuarterValues[id])
      if ((feeToUpdate.type === "Annually" || feeToUpdate.type === "Monthly") && feeQuarterValues[id]) {
        updateData.quarter_details = feeQuarterValues[id]
      }

      const response = await fetch(API_ENDPOINTS.fee(id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify(updateData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to update fee")
      }

      setFees((prevFees) =>
        prevFees.map((fee) =>
          fee.id === id ? { ...fee, changed: false } : fee
        )
      )

      toast.success("Fee updated successfully")
    } catch (err) {
      toast.error(err.message || "Failed to update fee")
    }
  }

  const handleDeleteFee = async (id) => {
    try {
      const response = await fetch(API_ENDPOINTS.fee(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to delete fee")
      }

      setFees((prevFees) => prevFees.filter((fee) => fee.id !== id))
      setExpandedFeeId(null)
      
      // Remove quarter values for this fee
      setFeeQuarterValues(prev => {
        const updated = { ...prev }
        delete updated[id]
        return updated
      })
      
      toast.success("Fee deleted successfully")
    } catch (err) {
      toast.error(err.message || "Failed to delete fee")
    }
  }

  const calculateQuarterSum = () => {
    return quarterValues.reduce((sum, val) => sum + (Number(val) || 0), 0)
  }

  const calculateFeeQuarterSum = (feeId) => {
    if (!feeQuarterValues[feeId]) return 0
    return feeQuarterValues[feeId].reduce((sum, val) => sum + (Number(val) || 0), 0)
  }

  const distributeEvenly = () => {
    if (newFeeTypeSelect === "Annually" && newFeeAmount) {
      const amount = Number(newFeeAmount)
      const evenAmount = amount / 4
      setQuarterValues([evenAmount, evenAmount, evenAmount, evenAmount])
    } else if (newFeeTypeSelect === "Monthly") {
      setQuarterValues([3, 3, 3, 3])
    }
  }

  const toggleExpandFee = (feeId) => {
    setExpandedFeeId(expandedFeeId === feeId ? null : feeId)
  }

  const getQuarterPlaceholder = (feeType, qtr, feeAmount) => {
    if (feeType === "Monthly") {
      return `${qtr} months (default 3)`
    } else if (feeType === "Annually") {
      return `${qtr} amount (default ${Math.round(feeAmount / 4 || 0)})`
    }
    return qtr
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="w-full md:w-1/3">
          <label className="block text-sm font-medium mb-1">
            Current Academic Year: {academicYears[0]?.startYear}-{academicYears[0]?.endYear}
          </label>
        </div>
        <div className="w-full md:w-1/3">
          <label className="block text-sm font-medium mb-1">Select class</label>
          <Select value={selectedClass} onValueChange={setSelectedClass} disabled={!classes.length}>
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder={classes.length ? "Select class" : "Loading classes..."} />
            </SelectTrigger>
            <SelectContent>
              {classes.map((cls) => (
                <SelectItem key={cls._id} value={cls._id} className="text-sm">
                  {cls.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <p className="text-gray-500">Loading fees...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-2 text-sm font-medium text-gray-500 w-10">#</th>
                <th className="text-left py-2 px-2 text-sm font-medium text-gray-500">Fee Name</th>
                <th className="text-left py-2 px-2 text-sm font-medium text-gray-500">Fee Amount</th>
                <th className="text-left py-2 px-2 text-sm font-medium text-gray-500">Fees calculates per</th>
                <th className="text-left py-2 px-2 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fees.length > 0 ? (
                fees.map((fee, index) => (
                  <>
                    <tr key={fee.id} className="border-b">
                      <td className="py-3 px-2 text-sm">{index + 1}.</td>
                      <td className="py-3 px-2">
                        <Input
                          value={fee.name}
                          onChange={(e) => handleInputChange(fee.id, "name", e.target.value)}
                          className="border-0 focus-visible:ring-0 p-0 h-8 text-sm"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <Input
                          type="number"
                          value={fee.amount}
                          onChange={(e) => handleInputChange(fee.id, "amount", e.target.value)}
                          className="border-0 focus-visible:ring-0 p-0 h-8 text-sm"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center">
                          <Select value={fee.type} onValueChange={(val) => handleInputChange(fee.id, "type", val)}>
                            <SelectTrigger className="border-0 focus:ring-0 shadow-none h-8 p-0 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Monthly">Monthly</SelectItem>
                              <SelectItem value="Quarterly">Quarterly</SelectItem>
                              <SelectItem value="Annually">Annually</SelectItem>
                            </SelectContent>
                          </Select>
                          {(fee.type === "Annually" || fee.type === "Monthly") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="ml-2 p-0 h-8 w-8"
                              onClick={() => toggleExpandFee(fee.id)}
                            >
                              {expandedFeeId === fee.id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 flex space-x-2">
                        {fee.changed ? (
                          <Button 
                            size="sm" 
                            onClick={() => handleUpdateFee(fee.id)} 
                            className="h-8 text-xs"
                            disabled={validateFeeQuarterValues(fee.id, fee.amount, fee.type)}
                          >
                            Update
                          </Button>
                        ) : (
                          <span className="text-sm text-gray-500">No changes</span>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteFee(fee.id)}
                          className="h-8 text-xs"
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                    {(fee.type === "Annually" || fee.type === "Monthly") && expandedFeeId === fee.id && (
                      <tr className="border-b bg-gray-50">
                        <td></td>
                        <td colSpan={4} className="px-2 py-3">
                          <div>
                            <div className="grid grid-cols-4 gap-2 mb-2">
                              {["Q1", "Q2", "Q3", "Q4"].map((qtr, idx) => (
                                <Input
                                  key={qtr}
                                  type="number"
                                  value={feeQuarterValues[fee.id]?.[idx] || ""}
                                  placeholder={getQuarterPlaceholder(fee.type, qtr, fee.amount)}
                                  className={`h-8 text-sm ${
                                    validateFeeQuarterValues(fee.id, fee.amount, fee.type)
                                      ? "border-red-500 focus-visible:ring-red-500" 
                                      : ""
                                  }`}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value || 0)
                                    handleQuarterValueChange(fee.id, idx, val)
                                  }}
                                />
                              ))}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                {validateFeeQuarterValues(fee.id, fee.amount, fee.type) ? (
                                  <Alert variant="destructive" className="py-2 bg-red-50 border-red-200">
                                    <AlertDescription className="text-xs text-red-600">
                                      {validateFeeQuarterValues(fee.id, fee.amount, fee.type)}
                                    </AlertDescription>
                                  </Alert>
                                ) : (
                                  <div className="text-sm text-gray-600">
                                    {fee.type === "Annually" ? (
                                      <>
                                        Total: {calculateFeeQuarterSum(fee.id)} / {fee.amount}
                                        {calculateFeeQuarterSum(fee.id) > 0 && 
                                         Math.abs(calculateFeeQuarterSum(fee.id) - Number(fee.amount)) < 0.01 && (
                                          <span className="ml-2 text-green-600">✓ Amounts match</span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        Total months: {calculateFeeQuarterSum(fee.id)} / 12
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-8 text-xs ml-2"
                                onClick={() => distributeEvenlyForFee(fee.id)}
                              >
                                Distribute Evenly
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-gray-500">
                    No fees found for this class and academic year
                  </td>
                </tr>
              )}

              {newFeeType && (
                <>
                  <tr className="border-b bg-gray-50">
                    <td className="py-3 px-2 text-sm">{fees.length + 1}.</td>
                    <td className="py-3 px-2">
                      <Input
                        value={newFeeName}
                        onChange={(e) => setNewFeeName(e.target.value)}
                        placeholder="Enter fee name"
                        className="border-0 p-0 h-8 text-sm"
                      />
                    </td>
                    <td className="py-3 px-2">
                      <Input
                        type="number"
                        value={newFeeAmount}
                        onChange={(e) => setNewFeeAmount(e.target.value)}
                        placeholder="Enter amount"
                        className="border-0 p-0 h-8 text-sm"
                      />
                    </td>
                    <td className="py-3 px-2">
                      <Select value={newFeeTypeSelect} onValueChange={(val) => {
                        setNewFeeTypeSelect(val)
                        // Reset quarter values based on fee type
                        if (val === "Monthly") {
                          setQuarterValues([3, 3, 3, 3])
                        } else if (val === "Annually" && newFeeAmount) {
                          const amount = Number(newFeeAmount)
                          const evenAmount = amount / 4
                          setQuarterValues([evenAmount, evenAmount, evenAmount, evenAmount])
                        } else {
                          setQuarterValues([0, 0, 0, 0])
                        }
                      }}>
                        <SelectTrigger className="border-0 shadow-none h-8 p-0 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Quarterly">Quarterly</SelectItem>
                          <SelectItem value="Annually">Annually</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-2 flex space-x-2">
                      <Button 
                        size="sm" 
                        onClick={handleSaveNewType} 
                        className="h-8 text-xs"
                        disabled={quarterError}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCancelNewType} className="h-8 text-xs">
                        Cancel
                      </Button>
                    </td>
                  </tr>

                  {(newFeeTypeSelect === "Monthly" || newFeeTypeSelect === "Annually") && (
                    <>
                      <tr className="border-b bg-gray-50">
                        <td></td>
                        <td colSpan={4} className="px-2 py-2">
                          <div className="grid grid-cols-4 gap-2">
                            {["Q1", "Q2", "Q3", "Q4"].map((qtr, idx) => (
                              <Input
                                key={qtr}
                                type="number"
                                value={quarterValues[idx] || ""}
                                placeholder={
                                  newFeeTypeSelect === "Monthly"
                                    ? `${qtr} months (default 3)`
                                    : `${qtr} amount (default ${Math.round(newFeeAmount / 4 || 0)})`
                                }
                                className={`h-8 text-sm ${
                                  quarterError 
                                    ? "border-red-500 focus-visible:ring-red-500" 
                                    : ""
                                }`}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value || 0)
                                  setQuarterValues((prev) => {
                                    const updated = [...prev]
                                    updated[idx] = val
                                    return updated
                                  })
                                }}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>

                      <tr className="border-b bg-gray-50">
                        <td></td>
                        <td colSpan={4} className="px-2 py-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              {quarterError ? (
                                <Alert variant="destructive" className="py-2 bg-red-50 border-red-200">
                                  <AlertDescription className="text-xs text-red-600">
                                    {quarterError}
                                  </AlertDescription>
                                </Alert>
                              ) : (
                                <div className="text-sm text-gray-600">
                                  {newFeeTypeSelect === "Annually" ? (
                                    <>
                                      Total: {calculateQuarterSum()} / {newFeeAmount}
                                      {calculateQuarterSum() > 0 && 
                                       Math.abs(calculateQuarterSum() - Number(newFeeAmount)) < 0.01 && (
                                        <span className="ml-2 text-green-600">✓ Amounts match</span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      Total months: {calculateQuarterSum()} / 12
                                      {calculateQuarterSum() === 12 && (
                                        <span className="ml-2 text-green-600">✓ Months match</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-xs ml-2"
                              onClick={distributeEvenly}
                            >
                              Distribute Evenly
                            </Button>
                          </div>
                        </td>
                      </tr>
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>

          {!newFeeType && (
            <Button variant="ghost" className="mt-4 text-xs flex items-center" onClick={handleAddNewType}>
              <span className="mr-1">+</span> Add new type
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default FeeStructureTable
